import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';
import {
  CONDITION_MAP, getStickerValue, buildStickerIndex, withStickerImages,
  receivedValue, valueTradeNow, isTradedAway, findSkinImage,
} from './helpers';
import { buildLineage } from './tradeLineage';
import { useSettling, settlesInLabel } from './settlingSales';
import { useTradeHistory, findTradeEvent } from './tradeHistoryContext';
import { normalizeTradePartner } from './TradePartner';
import StickerSlots from './StickerSlots';
import {
  dayOf, useArchiveSeries, useArchivePrices, nowPrice, moveSince, readingNote,
} from './priceArchive';
import './ItemDetailModal.css';

// Detail modal shared by the Wishlist and Investments cards (both themes).
// It answers the two questions a card can't: what has this skin done over
// time, and where is it cheapest right now.
//
// Price history lives on the item itself — rows store
// { date, buffPrice, csfloatPrice } and wishlist entries store
// { date, price, csfloatPrice } — so the chart reads whatever the daily
// snapshot recorded and falls back to the legacy single-price shape.

const RANGES = [
  { key: '7d',  label: '7D',  days: 7 },
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
  // Only worth offering because of the archive — the app's own snapshots have
  // never gone back a year for anything.
  { key: '1y',  label: '1Y',  days: 365 },
  { key: 'all', label: 'All', days: null },
];

const SOURCES = [
  { key: 'buff', label: 'Buff163' },
  { key: 'csf',  label: 'CSFloat' },
  // The archive: CSFloat readings back to 2023, independent of when this row
  // was created. The other two only exist for days this app was running and
  // watching this item.
  { key: 'arc',  label: 'History' },
  { key: 'both', label: 'All' },
];

// Chart colours can't come from CSS variables (recharts paints to SVG attrs),
// so each theme gets an explicit palette.
const SKIN = {
  zone: {
    buff: '#0d7a43', csf: '#6d3bd8', arc: '#b45309', era: '#131313', ref: '#c8134b', ref2: '#0d7a43',
    grid: 'rgba(19,19,19,0.14)',
    tick: { fill: '#131313', fontSize: 14.5, fontWeight: 800, fontFamily: 'Archivo, sans-serif' },
    axis: { stroke: '#131313', strokeWidth: 3 },
    cursor: { stroke: '#131313', strokeWidth: 2, strokeDasharray: '4 4' },
    strokeWidth: 3,
  },
  classic: {
    buff: '#4ade80', csf: '#818cf8', arc: '#fbbf24', era: 'rgba(255,255,255,0.35)', ref: '#f59e0b', ref2: '#38bdf8',
    grid: 'rgba(255,255,255,0.06)',
    tick: { fill: '#6b7280', fontSize: 14, fontWeight: 600 },
    axis: false,
    cursor: { stroke: 'rgba(255,255,255,0.25)', strokeWidth: 1, strokeDasharray: '4 4' },
    strokeWidth: 2,
  },
};

// Legend and summary names for the three lines. `arc` is called "CSFloat
// history" rather than just CSFloat so it is never mistaken for the live
// CSFloat snapshot line sitting next to it.
const LINE = { buff: 'Buff163', csf: 'CSFloat', arc: 'CSFloat history' };

const money = (n) => Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signedMoney = (n) => ((Number(n) || 0) >= 0 ? '+$' : '-$') + money(n);
const signedPct = (n) => `${(Number(n) || 0) >= 0 ? '+' : '-'}${Math.abs(Number(n) || 0).toFixed(1)}%`;
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'n/a');

export function marketHashName(skin, condition) {
  if (!skin) return '';
  if (!condition) return skin;
  return `${skin} (${CONDITION_MAP[condition] || condition})`;
}

/** Union of several rows' priceHistory arrays, one entry per date. */
export function mergeHistories(histories = []) {
  const byDate = new Map();
  histories.flat().forEach(p => {
    if (!p?.date) return;
    const e = byDate.get(p.date) || { date: p.date, buffPrice: null, csfloatPrice: null };
    if (e.buffPrice == null) e.buffPrice = p.buffPrice ?? p.price ?? null;
    if (e.csfloatPrice == null) e.csfloatPrice = p.csfloatPrice ?? null;
    byDate.set(p.date, e);
  });
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/**
 * One item on either side of a trade: picture, name, and what it is worth.
 *
 * Clickable only when the caller passed onOpenRow — the overview has to stay
 * readable in the tabs that never wired it up, so a missing handler degrades to
 * plain text rather than a button that does nothing.
 */
function TradeItem({ entry, skinsData, money: value, note, onOpen, current = false, pending = false }) {
  if (!entry?.row) {
    // Rows imported before trades were auto-linked name a trade that nothing in
    // the tracker answers to. Saying so beats an empty slot pretending the
    // trade had only one side.
    return <div className="idm-ti gone">item no longer in the tracker</div>;
  }
  const r = entry.row;
  const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
  const clickable = !!onOpen && !current;

  const inner = (
    <>
      {img
        ? <img src={img} alt="" loading="lazy" />
        : <span className="idm-ti-blank" />}
      <span className="idm-ti-txt">
        <span className="n">{r.skin?.value || 'Unnamed item'}</span>
        <span className="s">{r.condition?.value || 'n/a'}{note ? ` · ${note}` : ''}</span>
      </span>
      {value != null && <span className="idm-ti-v">{value}</span>}
    </>
  );

  // The item the modal is open on is marked rather than linked — clicking it
  // would reload the panel you are already looking at.
  const cls = pending ? " pending" : "";
  if (current) return <div className={`idm-ti current${cls}`}>{inner}<span className="idm-ti-you">this one</span></div>;
  if (!clickable) return <div className={`idm-ti${cls}`}>{inner}</div>;
  return (
    <button type="button" className={`idm-ti link${cls}`} onClick={() => onOpen(r, entry.index)}>
      {inner}
    </button>
  );
}

const EXTERIOR_IN_NAME = /\s*\((Factory New|Minimal Wear|Field[- ]Tested|Well[- ]Worn|Battle[- ]Scarred)\)\s*$/i;

/**
 * An item known only from Steam's trade history: handed over before it was
 * ever tracked, so there is no row to open or price. Drawn in the same frame
 * as a tracked item, marked so it never reads as one.
 */
function HistoryItem({ item, skinsData }) {
  const raw  = String(item?.name || '');
  const name = raw.replace(EXTERIOR_IN_NAME, '').trim();
  const cond = (item?.condition || EXTERIOR_IN_NAME.exec(raw)?.[1] || '').replace(/-/g, ' ') || null;
  const img  = findSkinImage(skinsData, name, cond);
  return (
    <div className="idm-ti">
      {img
        ? <img src={img} alt="" loading="lazy" />
        : <span className="idm-ti-blank" />}
      <span className="idm-ti-txt">
        <span className="n">{name || 'Unnamed item'}</span>
        <span className="s">{cond || 'n/a'} · not tracked</span>
      </span>
    </div>
  );
}

/**
 * The end of the chain, when the item left for money rather than for an item.
 *
 * WHY THIS EXISTS
 * The forward walk only follows TRADES (tradeLineage.walkForward requires
 * isTradedAway), so an item received in a trade and then SOLD had no forward
 * step at all: the chain showed how you got it and then stopped, as though it
 * were still sitting in your locker. For a settling sale that is doubly wrong —
 * the row genuinely still looks live, so nothing anywhere in the panel said the
 * item was on its way out.
 *
 * A sale is not a trade and is deliberately not drawn as one: there is no "got
 * back" side, only money and a date. It reuses the hop's frame so the chain
 * still reads as one sequence.
 */
function SaleStep({ row, skinsData, sale, settling }) {
  const gross = settling ? settling.price : (parseFloat(row?.sellPrice) || 0);
  const when  = sale?.at ? new Date(sale.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const net   = !settling && parseFloat(row?.profit) ? parseFloat(row.profit) : null;
  const settles = settling ? settlesInLabel(settling.settlesAt) : null;

  return (
    <div className={`idm-trade${settling ? ' pending' : ''}`}>
      <div className="idm-trade-side">
        <div className="idm-trade-lbl">{settling ? 'Selling' : 'Sold'}</div>
        <TradeItem
          entry={{ row, index: -1 }}
          skinsData={skinsData}
          note="this one"
          money={`$${money(parseFloat(row?.buyPrice) || 0)}`}
          pending={!!settling}
        />
      </div>

      <div className="idm-trade-mid" aria-hidden="true">
        <span className="arrow">&#8594;</span>
        <span className="when">{when || 'date unknown'}</span>
        <span className="who">{sale?.market || 'CSFloat'}</span>
      </div>

      <div className="idm-trade-side">
        <div className="idm-trade-lbl">{settling ? 'Money on its way' : 'Money back'}</div>
        <div className={`idm-sale-end${settling ? ' pending' : ''}`}>
          <span className="v">${money(gross)}</span>
          <span className="s">
            {settling
              // Named rather than just "pending": the useful fact is when it
              // stops being reversible, which is when the row becomes a sale.
              ? (settles ? `${settles} · not final yet` : 'inside trade protection · not final yet')
              : (net != null ? `${net >= 0 ? '+' : '−'}$${money(net)} after fees` : 'gross')}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * One trade, drawn as what went out against what came back.
 *
 * This is the answer to "where did this item come from" and "what did it turn
 * into" in one shape, used for both directions: walking backwards, `gave` is
 * the item that paid for the one you are looking at; walking forwards, `gave`
 * IS the one you are looking at. Marking the current row on whichever side it
 * falls is what keeps the two readings from being confusable.
 */
function TradeOverview({
  gave, got, at, partner, boot, promised = [], event = null,
  currentRow, skinsData, livePrices, onOpen, giveNote, settlingOf,
}) {
  const when = at
    ? new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'date unknown';

  // A list since one trade can hand over several items (see tradeLineage.js).
  // It used to be passed straight to TradeItem as if it were one entry, and an
  // array has no `.row`, so every backwards hop said the item was gone even
  // when its row was right there.
  const gaveList = Array.isArray(gave) ? gave : (gave ? [gave] : []);
  // Only when no row answers for that side: the history is the fallback, never
  // a second copy of what the rows already show.
  const fromHistory = gaveList.length ? [] : (event?.itemsGiven || []);
  const gaveCount = gaveList.length || fromHistory.length;

  return (
    <div className="idm-trade">
      <div className="idm-trade-side">
        <div className="idm-trade-lbl">Gave away{gaveCount > 1 ? ` (${gaveCount})` : ''}</div>
        {gaveList.length > 0 ? gaveList.map((e, i) => (
          <TradeItem
            key={`${e.index}-${i}`}
            entry={e}
            skinsData={skinsData}
            note={giveNote}
            money={e?.row ? `$${money(parseFloat(e.row.buyPrice) || 0)}` : null}
            onOpen={onOpen}
            current={!!e?.row && e.row === currentRow}
          />
        )) : fromHistory.length > 0 ? fromHistory.map((item, i) => (
          <HistoryItem key={i} item={item} skinsData={skinsData} />
        )) : (
          <TradeItem entry={null} />
        )}
      </div>

      <div className="idm-trade-mid" aria-hidden="true">
        <span className="arrow">&#8594;</span>
        <span className="when">{when}</span>
        {partner && <span className="who">{partner.name}</span>}
        {/* Signed: cash can move either way, and a trade that only ever showed
            money coming in would hide half of what actually happens. */}
        {boot !== 0 && (
          <span className={`cash ${boot > 0 ? 'in' : 'out'}`}>
            {boot > 0 ? `+$${money(boot)}` : `-$${money(Math.abs(boot))}`}
          </span>
        )}
      </div>

      <div className="idm-trade-side">
        <div className="idm-trade-lbl">Got back{got.length > 0 ? ` (${got.length})` : ''}</div>
        {got.length > 0 ? got.map(e => {
          const v = receivedValue(e.row, livePrices);
          // A sale that is agreed but still inside Steam's trade-protection
          // window. The row is untouched on purpose — the money can still be
          // rolled back — so without this the chain draws an item you have
          // effectively sold as one you are still holding, priced at market.
          //
          // Shown at the agreed price rather than today's, because that is the
          // number that will land, and marked so it never reads as settled.
          const settling = !v.sold ? settlingOf?.(e.row) : null;
          return (
            <TradeItem
              key={e.index}
              entry={e}
              skinsData={skinsData}
              money={settling
                ? `$${money(settling.price)}`
                : (v.value != null ? `$${money(v.value)}` : 'n/a')}
              note={v.sold ? 'sold' : (settling ? (settlesInLabel(settling.settlesAt) || 'sold · settling') : null)}
              pending={!!settling}
              onOpen={onOpen}
              current={e.row === currentRow}
            />
          );
        }) : (
          <div className="idm-trade-none">
            {promised.length > 0
              ? `Logged as traded for ${promised.map(x => x.name).filter(Boolean).join(', ')}, but no rows were ever created for them.`
              : 'Nothing was recorded as coming back. If this was a lend, the Traded tab can turn it into one.'}
          </div>
        )}
      </div>
    </div>
  );
}

/** The little chart button that opens this modal from a card whose own click
 *  is already spoken for (Live/Sold open the editor, Locker toggles select). */
export function DetailButton({ onOpen, corner = true, className = '', title = 'Price history & marketplaces' }) {
  return (
    <button
      type="button"
      className={`idm-peek${corner ? ' corner' : ''}${className ? ` ${className}` : ''}`}
      title={title}
      aria-label={title}
      onClick={e => { e.stopPropagation(); onOpen(); }}
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19V5" /><path d="M4 19h16" /><path d="M7 15l4-5 3 3 5-7" />
      </svg>
    </button>
  );
}

/**
 * True when a row has a trade on either side of it, so a card can decide
 * whether to offer the trade button at all.
 */
export function hasTrade(row) {
  return !!(row?.receivedFromTradeId || (row?.tradeId && row?.tradedFor?.length > 0));
}

/**
 * The second corner button, for items that came out of (or went into) a trade.
 *
 * Its own control rather than a change to DetailButton, because the two answer
 * different questions and the chart icon has never suggested "where did this
 * come from". Clicking a card opens the editor, so without this the trade
 * overview was reachable only by guessing that the chart icon also held it.
 */
export function TradeButton({ onOpen, corner = true, className = '', title = 'What this was traded for' }) {
  return (
    <button
      type="button"
      className={`idm-peek trade${corner ? ' corner' : ''}${className ? ` ${className}` : ''}`}
      title={title}
      aria-label={title}
      onClick={e => { e.stopPropagation(); onOpen(); }}
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {/* Two arrows swapping places — the same idea the Traded tab draws with
          its left-to-right flow, shrunk to a 13px icon. */}
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 8h13" /><path d="M14 5l3 3-3 3" />
        <path d="M20 16H7" /><path d="M10 13l-3 3 3 3" />
      </svg>
    </button>
  );
}

export default function ItemDetailModal({
  variant = 'zone',
  kind: kindProp = 'wish',  // wish | position | item | sold | inventory | lend
  name, condition, image,
  history = [],
  livePrices, csfloatPrices,
  // wishlist
  targetPrice, note, createdAt,
  // position
  qty, cost, avg, days, units = [],
  // single row (item / sold) — history comes off the row too
  row,
  // Every tracker row, so the trade chain around this one can be walked. An
  // item traded away no longer appears in Live (see isTradedAway in helpers),
  // which makes this the place its story stays readable. Optional: callers that
  // have no row list simply get no chain.
  rows = null,
  // Lets a chain entry re-centre the modal on that item. Optional — without it
  // the chain still reads, it just does not click through.
  onOpenRow = null,
  // steam inventory asset
  float, paintSeed, tradeLock,
  // lend — the loan record and what the item was worth the day it went out
  lend, lentPrice = null,
  // Stickers rebuilt from trade history carry names but no pictures, so the
  // strip needs the skins database to fill them in. Callers that already keep
  // an index hand it over; the rest pass skinsData and one is built here.
  stickerIndex: stickerIndexProp, skinsData,
  onClose,
}) {
  const [range, setRange] = useState('all');

  const stickerIndex = useMemo(
    () => stickerIndexProp || buildStickerIndex(skinsData),
    [stickerIndexProp, skinsData],
  );
  // null means "whatever this item has the most of" — resolved below once the
  // archive answers. An explicit click pins it.
  const [sourceChoice, setSourceChoice] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // A seventh kind, worked out here rather than asked of every caller: an item
  // traded away is not a holding, and the "item" tiles would price it as one —
  // "Unrealized +$140" on something you handed to someone else last month is
  // the exact fiction this whole change exists to remove. Callers still pass
  // `item`; only the tiles and the anchor change.
  const kind = kindProp === 'item' && isTradedAway(row) ? 'traded' : kindProp;

  const mhn = marketHashName(name, condition);
  const c   = SKIN[variant] || SKIN.zone;

  const buffNow = livePrices
    ? (livePrices[mhn] || livePrices[name])?.starting_at?.price ?? null
    : null;
  const csfNow = csfloatPrices
    ? (csfloatPrices[mhn] || csfloatPrices[name])?.price ?? null
    : null;

  // ── The archive ─────────────────────────────────────────
  // Everything above this line only knows what this app happened to record
  // while it was running and while this row existed. The archive knows the
  // CSFloat price of this skin on every day it was recorded since 2023, which
  // for most items is years before the row was created.
  const { series: arcPoints, first: arcFirst, total: arcTotal, available: arcOn, eraFrom } =
    useArchiveSeries(mhn, { enabled: !!mhn });

  // ── Series: snapshots, archive, and today's live prices ─
  const series = useMemo(() => {
    const byDate = new Map();
    const touch = (date) => {
      let e = byDate.get(date);
      if (!e) byDate.set(date, e = { date, buff: null, csf: null, arc: null });
      return e;
    };

    for (const p of history || []) {
      if (!p?.date) continue;
      const e = touch(p.date);
      if (e.buff == null) e.buff = p.buffPrice ?? p.price ?? null;
      if (e.csf == null)  e.csf  = p.csfloatPrice ?? null;
    }
    for (const p of arcPoints || []) {
      if (p?.date != null) touch(p.date).arc = p.price;
    }

    const today = new Date().toISOString().slice(0, 10);
    if (buffNow != null || csfNow != null) {
      const e = touch(today);
      if (e.buff == null) e.buff = buffNow;
      if (e.csf == null)  e.csf  = csfNow;
      // The archive line runs to today off the live CSFloat feed: same market,
      // so extending it is a continuation rather than a splice.
      if (e.arc == null)  e.arc  = csfNow;
    }

    return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [history, arcPoints, buffNow, csfNow]);

  // One reading can't be drawn as a line, so a toggle stays off until its
  // source has at least two.
  const hasCsf = series.filter(p => p.csf != null).length >= 2;
  const hasArc = series.filter(p => p.arc != null).length >= 2;

  // History wins by default when it exists — it is the longest line on offer,
  // and the whole reason this modal is worth opening on an item bought in 2024.
  const source = sourceChoice || (hasArc ? 'arc' : 'buff');

  const chartData = useMemo(() => {
    const days_ = RANGES.find(r => r.key === range)?.days;
    if (!days_) return series;
    const cut = new Date(Date.now() - days_ * 86400000).toISOString().slice(0, 10);
    const windowed = series.filter(p => p.date >= cut);
    // Never render an empty chart just because the range is short.
    return windowed.length >= 2 ? windowed : series.slice(-2);
  }, [series, range]);

  // Calendar days actually on screen — the chart can only plot the days that
  // were snapshotted, which is usually fewer than the range button asks for.
  const spanDays = chartData.length >= 2
    ? Math.max(1, Math.round((+new Date(chartData[chartData.length - 1].date) - +new Date(chartData[0].date)) / 86400000))
    : 0;

  // Summary of the visible window, read off the active line.
  const key = source === 'both' ? (hasArc ? 'arc' : 'buff') : source;
  const summary = useMemo(() => {
    const vals = chartData.map(p => p[key]).filter(v => v != null);
    if (!vals.length) return null;
    const first = vals[0], last = vals[vals.length - 1];
    return {
      first, last,
      low: Math.min(...vals), high: Math.max(...vals),
      change: last - first,
      changePct: first ? (last - first) / first * 100 : null,
      points: vals.length,
    };
  }, [chartData, key]);

  // Where the archive changes from reconstructed sale medians to real listing
  // snapshots. Marked on the chart only when the visible window actually
  // crosses it, and pinned to a date that exists in the data — the X axis is
  // categorical, so a reference line on a date with no point would not render.
  const eraMark = useMemo(() => {
    if (!eraFrom || (key !== 'arc' && source !== 'both')) return null;
    const hit = chartData.find(p => p.date >= eraFrom && p.arc != null);
    return hit && chartData[0]?.date < eraFrom ? hit.date : null;
  }, [chartData, eraFrom, key, source]);

  // ── Marketplaces ────────────────────────────────────────
  const markets = [
    {
      id: 'buff', label: 'Buff163', price: buffNow,
      url: `https://buff.163.com/market/csgo#tab=selling&page_num=1&search=${encodeURIComponent(name || '')}`,
    },
    {
      id: 'csf', label: 'CSFloat', price: csfNow,
      url: `https://csfloat.com/search?market_hash_name=${encodeURIComponent(mhn)}`,
    },
    {
      id: 'steam', label: 'Steam Market', price: null, noFeed: true,
      url: `https://steamcommunity.com/market/listings/730/${encodeURIComponent(mhn)}`,
    },
  ];
  const priced  = markets.filter(m => m.price != null);
  const cheapest = priced.length ? Math.min(...priced.map(m => m.price)) : null;
  const spread = buffNow != null && csfNow != null && buffNow > 0
    ? (csfNow - buffNow) / buffNow * 100 : null;

  // ── Per-kind numbers ────────────────────────────────────
  const gapPct = kind === 'wish' && buffNow != null && targetPrice
    ? (buffNow - targetPrice) / targetPrice * 100 : null;
  const hit = kind === 'wish' && buffNow != null && targetPrice != null && buffNow <= targetPrice;

  const posValue = kind === 'position' && buffNow != null ? buffNow * (qty || 0) : null;
  const posPl    = posValue != null ? posValue - (cost || 0) : null;
  const posPct   = posPl != null && cost > 0 ? posPl / cost * 100 : null;

  // Single-row kinds (a held item, a sold ticket) read straight off the row.
  const buy      = parseFloat(row?.buyPrice) || 0;
  const sell     = parseFloat(row?.sellPrice) || 0;
  const netProfit = parseFloat(row?.profit) || 0;
  const unreal   = buffNow != null ? buffNow - buy : null;
  const unrealPct = unreal != null && buy > 0 ? unreal / buy * 100 : null;
  const roi      = sell > 0 && buy > 0 ? netProfit / buy * 100 : null;
  const heldDays = row?.timestamp
    ? Math.max(0, Math.floor((Date.now() - +new Date(row.timestamp)) / 86400000))
    : null;

  // ── The loan ────────────────────────────────────────────
  // A lend realizes nothing, so none of this touches profit. What it answers is
  // the one question the Lending page cannot fit on a card: the item has been
  // out for months — did the market move under it?
  const lendClosedAt = lend?.returnedAt || lend?.writtenOffAt || null;
  const lendDays = lend?.at
    ? Math.max(0, Math.floor(((lendClosedAt ? +new Date(lendClosedAt) : Date.now()) - +new Date(lend.at)) / 86400000))
    : null;
  const lendDueDays = lend?.dueAt ? Math.ceil((+new Date(lend.dueAt) - Date.now()) / 86400000) : null;
  const lendOverdue = !lendClosedAt && lendDueDays != null && lendDueDays < 0;

  // ── The chain ───────────────────────────────────────────
  // Where this item came from and what it turned into. Rebuilt only when the
  // row list or the row changes — walking it is cheap, indexing the whole
  // tracker to walk it is not.
  // Which rows have a CSFloat sale agreed but not yet out of trade protection.
  // Read from context rather than passed in: this modal is opened from fifteen
  // tabs, and the answer belongs to the account, not to any one of them.
  const settlingOf = useSettling();
  // Steam's own record of every trade, for the items the rows never held.
  const tradeEvents = useTradeHistory();

  // How the item's own story ends, when it ends in money rather than in another
  // item: a sale that has settled, or one still inside trade protection.
  //
  // The forward walk cannot answer this — it follows trades, and a sale is not
  // one — so the chain used to stop at "how you got this" for every item that
  // was sold. A settling sale is the sharper case: the row keeps no sell price
  // until the money is final, so nothing on it says the item is on its way out.
  const saleEnd = useMemo(() => {
    if (!row) return null;
    const settling = settlingOf(row);
    if (settling) {
      return { settling, sale: { at: settling.soldAt, market: 'CSFloat' } };
    }
    if (parseFloat(row.sellPrice) > 0) {
      return {
        settling: null,
        sale: {
          at: row.csfloatSale?.soldAt || row.soldAt || row.timestamp || null,
          market: row.siteFee?.label || row.siteFee?.value || null,
        },
      };
    }
    return null;
  }, [row, settlingOf]);

  const lineage = useMemo(
    () => (rows && row ? buildLineage(rows, row) : null),
    [rows, row],
  );

  // What the trade it left in is worth today, for the tiles below.
  const gone = useMemo(() => {
    if (kind !== 'traded' || !lineage?.exit) return null;
    return valueTradeNow(row, lineage.exit.received.map(r => r.row), livePrices);
  }, [kind, lineage, row, livePrices]);

  // ── "And what has it done since?" ───────────────────────
  // Every kind this modal serves has one date that matters — the day the thing
  // happened — and until the archive existed none of them could be priced
  // unless the app had been watching the item since. This is the same question
  // the Lending page asks, answered once here for all six.
  const anchor = useMemo(() => {
    switch (kind) {
      case 'lend':     return lend?.at ? { date: dayOf(lend.at), label: 'Market when lent', verb: 'since it went out' } : null;
      case 'sold':     return { date: dayOf(row?.csfloatSale?.soldAt || row?.soldAt || row?.timestamp), label: 'Market on sale day', verb: 'since you sold' };
      case 'wish':     return createdAt ? { date: dayOf(createdAt), label: 'Market when added', verb: 'since you added it' } : null;
      case 'item':     return { date: dayOf(row?.timestamp), label: 'Market when bought', verb: 'since you bought' };
      // The date on a traded row is the day the trade happened, not a purchase,
      // so the same reading answers a different question: what you would have
      // been holding if you had kept it.
      case 'traded':   return { date: dayOf(row?.timestamp), label: 'Market when traded', verb: 'since you traded it' };
      case 'position': {
        // The oldest unit opened the position, so that is the date the whole
        // holding has been exposed to the market from.
        const oldest = units.reduce((min, u) => {
          const t = +new Date(u?.timestamp);
          return Number.isNaN(t) ? min : Math.min(min, t);
        }, Infinity);
        return Number.isFinite(oldest) ? { date: dayOf(new Date(oldest)), label: 'Market when opened', verb: 'since you opened it' } : null;
      }
      default: return null;
    }
  }, [kind, lend, row, createdAt, units]);

  const today = new Date().toISOString().slice(0, 10);
  const archivePoints = useMemo(
    () => (mhn && anchor?.date ? [{ name: mhn, date: anchor.date }, { name: mhn, date: today }] : []),
    [mhn, anchor?.date, today],
  );
  const { at: archiveAt } = useArchivePrices(archivePoints);
  const anchorRead = anchor?.date ? archiveAt(mhn, anchor.date) : null;
  // "Now" comes from CSFloat because the archive is CSFloat. Comparing it
  // against the Buff figure in the tiles above would report the gap between two
  // marketplaces as if the item had moved.
  const archiveNow = nowPrice(csfloatPrices, mhn, archiveAt(mhn, today)?.price);
  const since = moveSince(anchorRead, archiveNow?.price);

  // The loan's own figures ride on the same answer. When the archive can price
  // the day it went out, both ends of the comparison are CSFloat; when it
  // cannot, this falls back to whatever the caller worked out from the row's
  // snapshots, measured against Buff — consistent either way, never mixed.
  const lentShown   = since ? since.then : lentPrice;
  const lendMove    = since ? since.change : (lentPrice != null && buffNow != null ? buffNow - lentPrice : null);
  const lendMovePct = since ? since.pct : (lendMove != null && lentPrice > 0 ? (lendMove / lentPrice) * 100 : null);

  // Where a trade-received row's cost actually comes from. Its buyPrice is a
  // share of what was handed over, weighted to what this item was worth on the
  // day — a real number, but not one anyone was ever invoiced for, so it says
  // so rather than sitting there looking like a purchase price.
  const basisNote = (() => {
    const b = row?.basisSplit;
    if (!b) return null;
    const when = shortDate(b.pricedOn || b.at);
    if (b.price == null) return 'share of a trade';
    const near = b.daysOff > 0 ? ` (nearest reading, ${b.daysOff}d off)` : '';
    return `share of a trade · worth $${money(b.price)} on ${when}${near}`;
  })();

  const STATS = {
    wish: [
      { k: 'Asking now',  v: buffNow != null ? `$${money(buffNow)}` : 'n/a', s: 'best Buff163 listing' },
      { k: 'Your target', v: targetPrice != null ? `$${money(targetPrice)}` : 'not set', s: hit ? 'reached' : 'waiting' },
      { k: 'Gap to target', v: gapPct != null ? signedPct(gapPct) : 'n/a',
        s: gapPct != null && buffNow != null && targetPrice != null ? `$${money(buffNow - targetPrice)} away` : 'n/a',
        tone: gapPct == null ? null : gapPct <= 0 ? 'win' : 'lose' },
      { k: 'Watching', v: createdAt ? `${Math.max(0, Math.floor((Date.now() - +new Date(createdAt)) / 86400000))}d` : 'n/a',
        s: `since ${shortDate(createdAt)}` },
    ],
    position: [
      { k: 'Units held', v: String(qty ?? 0), s: `avg $${money(avg)} each` },
      { k: 'Capital in', v: `$${money(cost)}`, s: `held ${days ?? 0}d` },
      { k: 'Value now',  v: posValue != null ? `$${money(posValue)}` : 'n/a', s: buffNow != null ? `$${money(buffNow)} per unit` : 'no live price' },
      { k: 'Position P/L', v: posPl != null ? signedMoney(posPl) : 'n/a',
        s: posPct != null ? `${signedPct(posPct)} on cost` : 'n/a',
        tone: posPl == null ? null : posPl >= 0 ? 'win' : 'lose' },
    ],
    item: [
      { k: 'You paid',   v: `$${money(buy)}`,
        s: basisNote || (row?.siteFee?.label ? row.siteFee.label.split(' ')[0] : 'cost basis') },
      { k: 'Market now', v: buffNow != null ? `$${money(buffNow)}` : 'n/a', s: 'best Buff163 listing' },
      { k: 'Unrealized', v: unreal != null ? signedMoney(unreal) : 'n/a',
        s: unrealPct != null ? `${signedPct(unrealPct)} on cost` : 'no live price',
        tone: unreal == null ? null : unreal >= 0 ? 'win' : 'lose' },
      { k: 'Held', v: heldDays != null ? `${heldDays}d` : 'n/a', s: `bought ${shortDate(row?.timestamp)}` },
    ],
    traded: [
      { k: 'You paid', v: `$${money(buy)}`, s: 'cost basis, now carried by what you got' },
      { k: 'Worth now', v: buffNow != null ? `$${money(buffNow)}` : 'n/a', s: 'if you had kept it' },
      { k: 'Got back', v: gone ? `$${money(gone.back)}` : 'n/a',
        s: gone
          ? `${gone.count} item${gone.count === 1 ? '' : 's'}${gone.unpriced ? `, ${gone.unpriced} unpriced` : ''}`
          : 'nothing recorded' },
      // The only profit figure on this panel, and it belongs to the trade
      // rather than to any one item in it.
      { k: gone?.settled ? 'Trade profit' : 'Potential profit',
        v: gone ? signedMoney(gone.potential) : 'n/a',
        s: gone?.settled ? 'every item sold' : 'at today’s prices',
        tone: !gone ? null : gone.potential >= 0 ? 'win' : 'lose' },
    ],
    sold: [
      { k: 'You paid',  v: `$${money(buy)}`, s: basisNote || 'cost basis' },
      { k: 'Sold for',  v: `$${money(sell)}`, s: row?.siteFee?.label ? `on ${row.siteFee.label.split(' ')[0]}` : 'gross' },
      { k: 'Profit',    v: signedMoney(netProfit), s: roi != null ? `${signedPct(roi)} after fees` : 'after fees',
        tone: netProfit >= 0 ? 'win' : 'lose' },
      { k: 'Worth now', v: buffNow != null ? `$${money(buffNow)}` : 'n/a',
        s: buffNow != null && sell > 0
          ? `${signedPct((buffNow - sell) / sell * 100)} since you sold`
          : 'no live price' },
    ],
    lend: [
      { k: 'You paid', v: `$${money(buy)}`, s: 'cost basis, unchanged by the loan' },
      { k: 'When lent', v: lentShown != null ? `$${money(lentShown)}` : 'n/a',
        s: anchorRead ? readingNote(anchorRead) : lend?.at ? `market on ${shortDate(lend.at)}` : 'no date recorded' },
      { k: 'Market now', v: buffNow != null ? `$${money(buffNow)}` : 'n/a',
        s: buffNow != null ? 'best Buff163 listing' : 'no price feed for this item' },
      { k: 'Since lent', v: lendMove != null ? signedMoney(lendMove) : 'n/a',
        s: lendMovePct != null
          ? `${signedPct(lendMovePct)} over ${lendDays ?? 0} day${lendDays === 1 ? '' : 's'}`
          : lentShown == null ? 'no price the day it went out' : 'no live price to compare',
        tone: lendMove == null ? null : lendMove >= 0 ? 'win' : 'lose' },
    ],
    inventory: [
      { k: 'Buff163',  v: buffNow != null ? `$${money(buffNow)}` : 'n/a', s: 'best listing' },
      { k: 'CSFloat',  v: csfNow != null ? `$${money(csfNow)}` : 'n/a', s: spread != null ? `${signedPct(spread)} vs Buff` : 'no listing' },
      { k: 'Float',    v: float != null ? Number(float).toFixed(6) : 'n/a', s: float != null ? `${(float * 100).toFixed(1)}% worn` : 'not a skin' },
      { k: 'Pattern',  v: paintSeed != null ? `#${paintSeed}` : 'n/a', s: tradeLock || 'tradable' },
    ],
  };
  const stats = STATS[kind] || STATS.wish;

  const REFS = {
    wish:      targetPrice != null ? [{ y: targetPrice, label: `target $${money(targetPrice)}` }] : [],
    position:  avg ? [{ y: avg, label: `avg cost $${money(avg)}` }] : [],
    item:      buy > 0 ? [{ y: buy, label: `paid $${money(buy)}` }] : [],
    traded:    buy > 0 ? [{ y: buy, label: `cost $${money(buy)}` }] : [],
    sold: [
      buy  > 0 && { y: buy,  label: `paid $${money(buy)}` },
      sell > 0 && { y: sell, label: `sold $${money(sell)}` },
    ].filter(Boolean),
    inventory: [],
    lend: [
      lentShown != null && { y: lentShown, label: `lent at $${money(lentShown)}` },
      buy > 0 && { y: buy, label: `paid $${money(buy)}` },
    ].filter(Boolean),
  };
  const refLines = REFS[kind] || [];

  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="idm-tip">
        <div className="d">{label}</div>
        {payload.map(p => (
          <div key={p.dataKey} className="r">
            <span className="dot" style={{ background: p.stroke }} />
            <span className="n">{p.name}</span>
            <span className="v">${money(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const body = (
    <div className={`idm-overlay ${variant}`} onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="idm-panel" role="dialog" aria-modal="true" aria-label={`${name} details`}>

        {/* ── Header ─────────────────────────────── */}
        <div className="idm-head">
          {image
            ? <img src={image} alt="" />
            : <div className="idm-thumb-blank" />}
          <div className="idm-head-txt">
            <div className="idm-name" title={name}>{name}</div>
            <div className="idm-sub">
              <span>{condition || (kind === 'inventory' ? 'Steam inventory' : 'Any condition')}</span>
              {kind === 'position' && <span>{qty} unit{qty !== 1 ? 's' : ''} · held {days ?? 0}d</span>}
              {kind === 'wish' && <span>watching since {shortDate(createdAt)}</span>}
              {kind === 'item' && <span>bought {shortDate(row?.timestamp)} · held {heldDays ?? 0}d</span>}
              {kind === 'traded' && <span>traded away {shortDate(row?.timestamp)}</span>}
              {kind === 'traded' && <span className="idm-flag">not yours any more</span>}
              {kind === 'sold' && <span>logged {shortDate(row?.timestamp)}</span>}
              {kind === 'lend' && (
                <span>
                  {lendClosedAt ? 'was with' : 'with'} {lend?.to?.name || 'someone'} · {lendDays ?? 0}d
                </span>
              )}
              {kind === 'lend' && lendOverdue && <span className="idm-flag bad">{Math.abs(lendDueDays)}d overdue</span>}
              {kind === 'lend' && lendClosedAt && (
                <span className="idm-flag">{lend?.writtenOffAt ? 'written off' : 'came back'}</span>
              )}
              {kind === 'inventory' && float != null && <span>float {Number(float).toFixed(6)}</span>}
              {kind === 'wish' && hit && <span className="idm-flag">target hit</span>}
              {kind === 'sold' && <span className={`idm-flag ${netProfit >= 0 ? '' : 'bad'}`}>{netProfit >= 0 ? 'win' : 'loss'}</span>}
              {kind === 'item' && row?.isInvestment && <span className="idm-flag">investment</span>}
              {/* Sold, but the money can still be rolled back. Said here because
                  the row keeps no sell price until it is final, so every other
                  line in this header describes an item you no longer really have. */}
              {saleEnd?.settling && (
                <span className="idm-flag warn">
                  sold · {settlesInLabel(saleEnd.settling.settlesAt) || 'settling'}
                </span>
              )}
            </div>
          </div>
          <button type="button" className="idm-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="idm-body">

          {/* ── Stat tiles ───────────────────────── */}
          <div className="idm-stats">
            {stats.map(s => (
              <div key={s.k} className="idm-stat">
                <div className="k">{s.k}</div>
                <div className={`v ${s.tone || ''}`}>{s.v}</div>
                <div className="s">{s.s}</div>
              </div>
            ))}
          </div>

          {/* ── What the market did since ────────── */}
          {/* The one line the app could not write before the archive existed.
              Deliberately shows both ends and the day each was measured on:
              before mid-2025 the archive only has the days a skin actually
              sold, so a 2024 purchase is usually priced off the nearest
              recorded day rather than the day itself, and printing that as
              "the price you bought at" would be a quiet lie. */}
          {since && (
            <div className="idm-since">
              <span className="lbl">{anchor.label}</span>
              <span className="pair">
                <strong>${money(since.then)}</strong>
                <span className="arrow">→</span>
                <strong>${money(since.now)}</strong>
              </span>
              <span className={`chg ${since.change >= 0 ? 'win' : 'lose'}`}>
                {signedMoney(since.change)} ({signedPct(since.pct)}) {anchor.verb}
              </span>
              <span className="note">
                {readingNote(anchorRead)}
                {archiveNow?.source === 'archive' ? ' · latest archive reading' : ''}
              </span>
            </div>
          )}

          {/* ── The chain ────────────────────────── */}
          {/* An item traded away leaves Live and every portfolio total, because
              its cost basis now sits on the items it became. This is what stops
              that from meaning "gone": from any card in the app you can read
              back to what paid for this and forward to what it turned into,
              however many trades ago. */}
          {(lineage?.has || (saleEnd?.settling && kind !== 'sold')) && (
            <div className="idm-section">
              <div className="idm-section-head">
                <span className="idm-section-title">
                  {!lineage?.has ? 'On its way out'
                    : lineage.origin.length > 0 && !lineage.exit ? 'How you got this' : 'Trade history'}
                </span>
                <span className="idm-section-note">
                  {lineage.origin.length > 0 && lineage.exit
                    ? `${lineage.origin.length + 1} trades in this chain`
                    : lineage.origin.length > 1
                      ? `${lineage.origin.length} trades back`
                      : lineage.origin.length === 1
                        ? 'what you gave, and everything that came back'
                        : `traded away · ${lineage.totals?.items ?? 0} item${lineage.totals?.items === 1 ? '' : 's'} came back`}
                </span>
              </div>

              {/* Backwards first, oldest trade at the top, so the panel reads
                  as a story ending in the item you have open. Each hop is the
                  same shape, which is what lets a three-trade chain be read
                  without re-learning the layout at every step. */}
              {(lineage?.origin || []).map((hop, i) => {
                // A hop with no offered row has no date or partner of its own;
                // the rows that came back carry both, copied off the same trade.
                const recv = hop.received?.find(e => e?.row)?.row;
                return (
                <React.Fragment key={hop.tradeId}>
                  {i > 0 && <div className="idm-trade-then">then</div>}
                  <TradeOverview
                    gave={hop.gave}
                    event={hop.gave?.length ? null : findTradeEvent(tradeEvents, hop)}
                    // Everything that came back, this item included — it is
                    // marked rather than filtered out, because "which of these
                    // five is the one I clicked" is half the question.
                    got={hop.received}
                    at={hop.at || recv?.timestamp || null}
                    partner={hop.partner || normalizeTradePartner(recv?.tradedWith)}
                    boot={hop.boot}
                    settlingOf={settlingOf}
                    currentRow={row}
                    skinsData={skinsData}
                    livePrices={livePrices}
                    onOpen={onOpenRow}
                  />
                </React.Fragment>
                );
              })}

              {/* Forwards: the trade this item left in. */}
              {lineage?.exit && (
                <>
                  {lineage.origin.length > 0 && <div className="idm-trade-then">then</div>}
                  <TradeOverview
                    gave={{ row, index: -1 }}
                    giveNote="this one"
                    got={lineage.exit.received}
                    promised={lineage.exit.promised}
                    at={lineage.exit.at}
                    partner={lineage.exit.partner}
                    boot={lineage.exit.boot}
                    settlingOf={settlingOf}
                    currentRow={row}
                    skinsData={skinsData}
                    livePrices={livePrices}
                    onOpen={onOpenRow}
                  />
                </>
              )}

              {/* Forwards, when the item left for money instead of for another
                  item. Without this the chain simply stopped after "how you got
                  it", which reads as "and I still have it" — wrong for a sale,
                  and most wrong for one still settling, where the row itself
                  still looks live. */}
              {saleEnd && (
                <>
                  {(lineage?.origin?.length > 0 || lineage?.exit) && <div className="idm-trade-then">then</div>}
                  <SaleStep
                    row={row}
                    skinsData={skinsData}
                    sale={saleEnd.sale}
                    settling={saleEnd.settling}
                  />
                </>
              )}
            </div>
          )}

          {/* ── The loan ─────────────────────────── */}
          {/* Rendered from the lend record alone, so this modal still says
              something for an item with no price feed at all — which is the
              exact case that used to open a panel of n/a. */}
          {kind === 'lend' && lend && (
            <div className="idm-section">
              <div className="idm-section-head">
                <span className="idm-section-title">The loan</span>
                <span className="idm-section-note">
                  {lendClosedAt
                    ? `out for ${lendDays ?? 0} day${lendDays === 1 ? '' : 's'}`
                    : 'still out · nothing here touches your profit'}
                </span>
              </div>
              <div className="idm-loan">
                <div className="idm-loan-row">
                  <span className="k">{lendClosedAt ? 'Was with' : 'With'}</span>
                  <span className="v">
                    {lend.to?.profileUrl
                      ? <a href={lend.to.profileUrl} target="_blank" rel="noreferrer">{lend.to.name}</a>
                      : (lend.to?.name || 'n/a')}
                    {lend.to?.steamId64 && <span className="idm-loan-tag">verified from Steam</span>}
                  </span>
                </div>
                <div className="idm-loan-row">
                  <span className="k">Lent on</span>
                  <span className="v">{shortDate(lend.at)}</span>
                </div>
                <div className="idm-loan-row">
                  <span className="k">Expected back</span>
                  <span className={`v ${lendOverdue ? 'lose' : ''}`}>
                    {lend.dueAt
                      ? `${shortDate(lend.dueAt)}${lendClosedAt ? '' : lendOverdue ? ` · ${Math.abs(lendDueDays)}d late` : ` · in ${lendDueDays}d`}`
                      : 'open ended'}
                  </span>
                </div>
                {lendClosedAt && (
                  <div className="idm-loan-row">
                    <span className="k">{lend.writtenOffAt ? 'Written off' : 'Returned'}</span>
                    <span className={`v ${lend.writtenOffAt ? 'lose' : 'win'}`}>{shortDate(lendClosedAt)}</span>
                  </div>
                )}
                {lend.float != null && (
                  <div className="idm-loan-row">
                    <span className="k">Float on the way out</span>
                    <span className="v mono">{Number(lend.float).toFixed(6)}</span>
                  </div>
                )}
                {lend.note && (
                  <div className="idm-loan-row">
                    <span className="k">Note</span>
                    <span className="v em">“{lend.note}”</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Stickers ─────────────────────────── */}
          {(row?.stickers || []).some(s => s?.name) && (() => {
            const worth = getStickerValue(row, livePrices);
            return (
              <div className="idm-section">
                <div className="idm-section-head">
                  <span className="idm-section-title">Stickers</span>
                  <span className="idm-section-note">
                    {worth.total > 0
                      ? `worth $${worth.total.toFixed(2)}${worth.complete ? '' : ' or more, not all priced'}`
                      : 'no prices for these yet'}
                  </span>
                </div>
                <StickerSlots stickers={withStickerImages(row.stickers, stickerIndex)} charms={row.charms}
                  prices={livePrices}
                  variant={variant === 'zone' ? 'zone' : 'classic'} maxWidth={330}
                  style={{ marginBottom: 0 }} />
              </div>
            );
          })()}

          {/* ── Price history ────────────────────── */}
          <div className="idm-section">
            <div className="idm-section-head">
              <span className="idm-section-title">Price history</span>
              <span className="idm-section-note">
                {arcOn && arcTotal > 0
                  ? `${arcTotal} day${arcTotal !== 1 ? 's' : ''} on record since ${shortDate(arcFirst)}`
                  : `${series.length} day${series.length !== 1 ? 's' : ''} recorded`}
              </span>
              <div className="idm-toggles push">
                {SOURCES.map(s => {
                  const disabled = (s.key === 'csf' && !hasCsf)
                    || (s.key === 'arc' && !hasArc)
                    || (s.key === 'both' && !hasCsf && !hasArc);
                  const why = s.key === 'arc'
                    ? 'No archive history for this item'
                    : 'No CSFloat snapshots recorded yet';
                  return (
                    <button key={s.key} type="button" disabled={disabled}
                      className={`idm-toggle ${source === s.key ? 'on' : ''}`}
                      onClick={() => !disabled && setSourceChoice(s.key)}
                      title={disabled ? why : undefined}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <div className="idm-toggles">
                {RANGES.map(r => (
                  <button key={r.key} type="button"
                    className={`idm-toggle ${range === r.key ? 'on' : ''}`}
                    onClick={() => setRange(r.key)}>{r.label}</button>
                ))}
              </div>
            </div>

            {chartData.length < 2 ? (
              <div className="idm-nochart">
                {arcOn
                  ? 'Not enough history yet. The price archive has no record of this item, so the chart fills in from the daily snapshot as you keep it.'
                  : 'Not enough history yet. Prices are snapshotted once a day, so the chart fills in as you keep the item.'}
              </div>
            ) : (
              <div className="idm-chart">
                <ResponsiveContainer width="100%" height={260}>
                  {source === 'both' ? (
                    <LineChart data={chartData} margin={{ top: 8, right: 18, left: -6, bottom: 0 }}>
                      <CartesianGrid stroke={c.grid} vertical={false} />
                      <XAxis dataKey="date" tick={c.tick} axisLine={c.axis} tickLine={false}
                        interval="preserveStartEnd" tickFormatter={d => d.slice(5)} minTickGap={24} />
                      <YAxis tick={c.tick} axisLine={c.axis} tickLine={false}
                        tickFormatter={v => `$${v}`} width={58} domain={['auto', 'auto']} />
                      <Tooltip content={<Tip />} cursor={c.cursor} />
                      <Legend iconType="plainline" wrapperStyle={{ fontSize: 14.5, fontWeight: 700, paddingTop: 8 }} />
                      {refLines.map((r, ri) => (
                        <ReferenceLine key={r.label} y={r.y} stroke={ri ? c.ref2 : c.ref} strokeWidth={2} strokeDasharray="5 4"
                          label={{ value: r.label, fill: ri ? c.ref2 : c.ref, fontSize: 14, fontWeight: 700,
                            position: ri ? 'insideBottomLeft' : 'insideTopLeft' }} />
                      ))}
                      {eraMark && (
                        <ReferenceLine x={eraMark} stroke={c.era} strokeWidth={1.5} strokeDasharray="3 4"
                          label={{ value: 'listed prices from here', fill: c.era, fontSize: 13.5, fontWeight: 700,
                            position: 'insideTopRight' }} />
                      )}
                      {hasArc && (
                        <Line type="monotone" name={LINE.arc} dataKey="arc" stroke={c.arc} strokeWidth={c.strokeWidth}
                          dot={false} connectNulls activeDot={{ r: 4, strokeWidth: 0 }} />
                      )}
                      <Line type="monotone" name={LINE.buff} dataKey="buff" stroke={c.buff} strokeWidth={c.strokeWidth}
                        dot={false} connectNulls activeDot={{ r: 4, strokeWidth: 0 }} />
                      {hasCsf && (
                        <Line type="monotone" name={LINE.csf} dataKey="csf" stroke={c.csf} strokeWidth={c.strokeWidth}
                          dot={false} connectNulls activeDot={{ r: 4, strokeWidth: 0 }} />
                      )}
                    </LineChart>
                  ) : (
                    <AreaChart data={chartData} margin={{ top: 8, right: 18, left: -6, bottom: 0 }}>
                      <defs>
                        <linearGradient id="idmGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={c[key]} stopOpacity={0.26} />
                          <stop offset="95%" stopColor={c[key]} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={c.grid} vertical={false} />
                      <XAxis dataKey="date" tick={c.tick} axisLine={c.axis} tickLine={false}
                        interval="preserveStartEnd" tickFormatter={d => d.slice(5)} minTickGap={24} />
                      <YAxis tick={c.tick} axisLine={c.axis} tickLine={false}
                        tickFormatter={v => `$${v}`} width={58} domain={['auto', 'auto']} />
                      <Tooltip content={<Tip />} cursor={c.cursor} />
                      {refLines.map((r, ri) => (
                        <ReferenceLine key={r.label} y={r.y} stroke={ri ? c.ref2 : c.ref} strokeWidth={2} strokeDasharray="5 4"
                          label={{ value: r.label, fill: ri ? c.ref2 : c.ref, fontSize: 14, fontWeight: 700,
                            position: ri ? 'insideBottomLeft' : 'insideTopLeft' }} />
                      ))}
                      {eraMark && (
                        <ReferenceLine x={eraMark} stroke={c.era} strokeWidth={1.5} strokeDasharray="3 4"
                          label={{ value: 'listed prices from here', fill: c.era, fontSize: 13.5, fontWeight: 700,
                            position: 'insideTopRight' }} />
                      )}
                      <Area type="monotone" name={LINE[key]} dataKey={key}
                        stroke={c[key]} strokeWidth={c.strokeWidth}
                        fill="url(#idmGrad)" dot={false} connectNulls activeDot={{ r: 4, strokeWidth: 0 }} />
                    </AreaChart>
                  )}
                </ResponsiveContainer>

                {summary && (
                  <div className="idm-reading">
                    <span className="src">{LINE[key]}</span>
                    <span>Low <strong>${money(summary.low)}</strong></span>
                    <span>High <strong>${money(summary.high)}</strong></span>
                    <span>Now <strong>${money(summary.last)}</strong></span>
                    <span className={summary.change >= 0 ? 'win' : 'lose'}>
                      {signedMoney(summary.change)}
                      {summary.changePct != null && ` (${signedPct(summary.changePct)})`}
                      {` over ${spanDays} day${spanDays !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Marketplaces ─────────────────────── */}
          <div className="idm-section">
            <div className="idm-section-head">
              <span className="idm-section-title">Marketplaces</span>
              <span className="idm-section-note">
                {spread != null
                  ? `CSFloat is ${signedPct(spread)} vs Buff163`
                  : 'live asking prices'}
              </span>
            </div>
            <div className="idm-markets">
              {markets.map(m => (
                <a key={m.id} className={`idm-market ${m.price != null && m.price === cheapest ? 'best' : ''}`}
                  href={m.url} target="_blank" rel="noreferrer">
                  <span className="mk">{m.label}</span>
                  <span className="mv">{m.price != null ? `$${money(m.price)}` : 'n/a'}</span>
                  <span className="ms">
                    {m.noFeed
                      ? 'no price feed'
                      : m.price == null
                        ? 'not listed'
                        : m.price === cheapest && priced.length > 1
                          ? 'cheapest here'
                          : buffNow != null && m.id !== 'buff'
                            ? `${signedPct((m.price - buffNow) / buffNow * 100)} vs Buff`
                            : 'reference price'}
                  </span>
                  <span className="mx">↗</span>
                </a>
              ))}
            </div>
          </div>

          {/* ── Units in the position ────────────── */}
          {kind === 'position' && units.length > 0 && (
            <div className="idm-section">
              <div className="idm-section-head">
                <span className="idm-section-title">Units</span>
                <span className="idm-section-note">every buy that makes up this position</span>
              </div>
              <div className="idm-units">
                {/* Float and pattern are what tell two units of the same skin
                    and condition apart, so they sit here rather than the
                    marketplace name, which the header already implies. */}
                <div className="idm-unit head">
                  <span>Bought</span><span>Float</span><span>Pattern</span>
                  <span>Cost</span><span>Value now</span><span>P/L</span>
                </div>
                {units.map((u, i) => {
                  const buy = parseFloat(u.buyPrice) || 0;
                  const pl  = buffNow != null ? buffNow - buy : null;
                  return (
                    <div key={u.id || i} className="idm-unit">
                      <span title={u.siteFee?.label || u.siteFee?.value || ''}>{shortDate(u.timestamp)}</span>
                      <span className="mono">{u.float != null ? Number(u.float).toFixed(6) : 'n/a'}</span>
                      <span className="mono">{u.paintSeed != null ? `#${u.paintSeed}` : 'n/a'}</span>
                      <span>${money(buy)}</span>
                      <span>{buffNow != null ? `$${money(buffNow)}` : 'n/a'}</span>
                      <span className={pl == null ? '' : pl >= 0 ? 'win' : 'lose'}>
                        {pl == null ? 'n/a' : signedMoney(pl)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {kind === 'wish' && note && (
            <div className="idm-note">“{note}”</div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
